import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Compass, Search, Menu, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ThemeToggle } from '@/components/theme-toggle';
import { SettingsSlideDown } from '@/components/SettingsSlideDown';
import { AccountControl } from '@/components/console/AccountControl';
import { NotificationBell } from '@/components/NotificationBell';
import { RoleSwitcher } from '@/components/RoleSwitcher';
import type { Ticket } from '@/lib/mock';

export type AppView = string;

export function AppHeader({
  tickets,
  now,
  onNavigate,
  onSearch,
}: {
  source: 'mock' | 'live';
  tickets: Ticket[];
  now: number;
  onNavigate: (v: AppView) => void;
  onNew: () => void;
  onSearch: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const { hasPermission, hasRole, user } = useAuth();
  
  const role = (user?.role || '').toLowerCase().trim();
  const email = (user?.email || '').toLowerCase().trim();
  const isSocUser = role.includes('soc') || 
    email.includes('achakkaravarthy') || 
    email.includes('ljayaraj') || 
    email.includes('hraj') || 
    email.includes('pramachandran');

  // Show navigation menu for any authorized user with dashboard/queue/reports/soc access
  const hasConsoleNav = isSocUser || hasPermission('ticket.view') || hasRole(['Super Admin', 'Admin', 'admin', 'Manager', 'Agent', 'Technician', 'Viewer', 'Auditor']);

  const navigate = useNavigate();
  const location = useLocation();
  const isSocPortal = location.pathname.startsWith('/admin/soc');

  return (
    <header className="flex h-16 flex-shrink-0 items-center gap-3 bg-surface shadow-neo px-6 relative z-30 border-b border-[rgba(255,255,255,0.05)] transition-colors">
      {hasConsoleNav && (
        <button 
          onClick={() => setMenuOpen(!menuOpen)} 
          className="flex h-10 w-10 items-center justify-center rounded-xl text-[var(--color-text)] opacity-70 hover:opacity-100 hover:text-[var(--color-primary)] transition-colors neo-interactive"
          title="Open Navigation Menu"
          aria-label="Open Navigation Menu"
        >
          <Menu className="h-6 w-6" />
        </button>
      )}
      <button onClick={() => onNavigate(isSocUser ? 'soc' : 'hub')} className="flex items-center gap-3 neo-interactive px-2 py-1 rounded-xl transition-transform">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl neo">
          <Compass className="h-5 w-5 text-[var(--color-primary)]" />
        </div>
        <div className="flex flex-col text-left">
          <span className="font-display text-lg font-bold tracking-tight text-theme leading-tight">Compass</span>
          {isSocUser && <span className="text-[9px] font-bold text-[var(--color-primary)] uppercase tracking-wider">SOC Portal</span>}
        </div>
      </button>

      {/* Global Back Navigation Button */}
      {(isSocPortal || isSocUser || location.pathname.startsWith('/admin')) && (
        <button
          onClick={() => {
            if (isSocPortal) {
              navigate('/user/dashboard');
            } else if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/user/dashboard');
            }
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border bg-card/60 hover:bg-muted text-xs font-semibold text-foreground transition-all neo-interactive shadow-xs"
          title="Back to User Dashboard"
        >
          <ArrowLeft className="h-4 w-4 text-[var(--color-primary)]" />
          <span className="hidden sm:inline">Back</span>
        </button>
      )}

      <nav className="flex items-center gap-1">
        {/* Navigation managed in permission-controlled Sidebar */}
      </nav>

      {!isSocUser && (
        <button
          onClick={onSearch}
          className="ml-6 hidden h-11 w-64 lg:w-80 shrink-0 items-center gap-3 rounded-2xl neo-inset px-4 text-sm font-medium text-muted-foreground whitespace-nowrap transition-all hover:text-theme focus:ring-2 focus:ring-[var(--color-primary)]/30 md:flex"
        >
          <Search className="h-5 w-5 shrink-0" />
          <span className="truncate">Jump to team or ticket…</span>
          <kbd className="ml-auto shrink-0 rounded-lg neo bg-transparent px-2 py-1 font-mono text-[10px] font-bold text-theme">⌘K</kbd>
        </button>
      )}

      <div className="ml-auto flex items-center gap-4">
        {!isSocUser && <RoleSwitcher />}
        <NotificationBell tickets={tickets} now={now} />
        <ThemeToggle />
        <AccountControl />
      </div>
      {hasConsoleNav && <SettingsSlideDown open={menuOpen} onClose={() => setMenuOpen(false)} />}
    </header>
  );
}
